import React from "react";
import { Box, CssBaseline } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  designFontFamily,
  designShell,
  designTheme,
} from "services/constants/designTokens";
import { HomeGreeting } from "./HomeGreeting";
import { ActionCentre } from "./ActionCentre";
import { AttendanceShiftKpis } from "./KpiCards/AttendanceShiftKpis";
import { LeaveBalanceKpi } from "./KpiCards/LeaveBalanceKpi";
import { PendingRequestsKpi } from "./KpiCards/PendingRequestsKpi";
import { QuickActionsSection } from "./QuickActionsSection";
import { UpcomingScheduleSection } from "./UpcomingScheduleSection";
import { MyRequestsSummarySection } from "./MyRequestsSummarySection";
import { RecentPayslipSection } from "./RecentPayslipSection";
import { CompanyAnnouncementsSection } from "./CompanyAnnouncementsSection";
import { UpcomingHolidaysSection } from "./UpcomingHolidaysSection";
import { PendingApprovalsSection } from "./PendingApprovalsSection";
import { useMyPendingRequests } from "./useMyPendingRequests";
import { HomeOverallCountProvider } from "./HomeOverallCountContext";

// Post-login Home dashboard (the "Home" item under My Workspace). Every card
// here is backed by a real endpoint the app already uses elsewhere:
//   - Attendance + Shift .... checkinfilter                         (My Zone > Status)
//   - Leave Balance ......... leavesummary                          (My Zone > My Leaves)
//   - Pending Requests ...... My_Zone/Apply/Over_time/overall-count  (consolidated)
//   - Recent Payslip ........ user-payslip-details                  (My Zone > Payslip)
//   - Upcoming Schedule ..... daily_user_shift_assignments          (My Zone > Shift)
//   - Upcoming Holidays ..... companyleave                          (leave calendar)
// Company Announcements is a placeholder until an announcements backend exists.
// The Pending Requests KPI, the My Requests Summary and the Action Centre all
// read ONE consolidated fetch (HomeOverallCountProvider ->
// My_Zone/Apply/Over_time/overall-count/) instead of the old ~14-call fan-out,
// so their totals can never drift apart and the page issues a single request.

const customHomeTheme = createTheme({
  ...designTheme,
  typography: {
    fontFamily: designFontFamily,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: designFontFamily,
        },
      },
    },
  },
});

const MyWorkspaceHomeContent = () => {
  const { countsByType, pending, total, loading } = useMyPendingRequests();

  return (
    <ThemeProvider theme={customHomeTheme}>
      <CssBaseline />
      <Box
        sx={{
          ...designShell.pageSurface,
          fontFamily: designFontFamily,
          py: { xs: 2, md: 2.5 },
          px: { xs: 2, md: 3 },
          minHeight: "100%",
          overflowX: "hidden",
        }}
      >
        <Box sx={{ mb: 2.5 }}>
          <HomeGreeting />
        </Box>

        <ActionCentre />

        {/* KPI row — CSS grid with auto-fit instead of MUI's fixed-width Grid
            (xs=12/sm=6/md=3 items): each of these four tiles hides itself when
            the signed-in user lacks viewer access to what it previews (Today's
            Attendance, Today's Shift, Leave Balance — Pending Requests always
            shows), and a fixed-width Grid just leaves blank space where a
            hidden tile's column used to be instead of letting the rest fill
            the row. auto-fit recomputes each tile's share of the row from
            however many actually rendered, with no need to know that count in
            advance. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 2.5,
          }}
        >
          <AttendanceShiftKpis />
          <LeaveBalanceKpi />
          <PendingRequestsKpi total={total} loading={loading} />
        </Box>

        {/* Quick Actions */}
        <Box sx={{ mt: 2.5 }}>
          <QuickActionsSection />
        </Box>

        {/* Row 2: Upcoming Schedule, My Requests Summary, Upcoming Holidays --
            same auto-fit reasoning as the KPI row above. All three now hide
            themselves for a user with no relevant access -- Upcoming
            Schedule/Upcoming Holidays for no Shift/Calendar access, My
            Requests Summary when none of its request types are viewer-
            accessible (e.g. "My Request" cut off entirely) -- and previously
            sat inside a fixed <Grid item md={4}> here that stayed in the
            layout, still reserving its column, even once the section inside
            it rendered null. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 2.5,
          }}
        >
          <UpcomingScheduleSection />
          <MyRequestsSummarySection
            countsByType={countsByType}
            pending={pending}
            loading={loading}
          />
          <UpcomingHolidaysSection />
        </Box>

        {/* Row 3: Recent Payslip (full width) */}
        <Box sx={{ mt: 2.5 }}>
          <RecentPayslipSection />
        </Box>

        {/* Company Announcements — kept below the fold (placeholder until a feed
            backend exists); image 2 leads with Payslip, this stays available. */}
        <Box sx={{ mt: 2.5 }}>
          <CompanyAnnouncementsSection />
        </Box>

        {/* Approver-only: hidden entirely for non-approvers (renders null) */}
        <Box sx={{ mt: 2.5 }}>
          <PendingApprovalsSection />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

// Wraps the dashboard in the provider that performs the single consolidated
// overall-count fetch, so every pending-count consumer on the page (the Pending
// Requests KPI, My Requests Summary and the Action Centre) shares one request.
const MyWorkspaceHome = () => (
  <HomeOverallCountProvider>
    <MyWorkspaceHomeContent />
  </HomeOverallCountProvider>
);

export default MyWorkspaceHome;
